import React, { ComponentPropsWithRef, forwardRef, useEffect } from 'react';
import { useShareForwardedRef } from '../../hooks/useShareForwardedRef';
import {
  WithErrorMessageComputedProps,
  WithErrorMessageProps,
  withErrorMessage
} from '../InputV2/withErrorMessage';
import { InputProps } from '../InputV2/Input.types';
import { InputBaseV2 } from '../InputV2';
import { useFieldAutoHeight } from '../InputV2/useFieldAutoHeight';

export type FieldTextareaProps = {
  autoHeight?: boolean;
} & ComponentPropsWithRef<typeof TextareaWithErrorMessage>;

const TextareaWithErrorMessage = withErrorMessage<
  HTMLTextAreaElement,
  WithErrorMessageProps & Omit<InputProps<'textarea'>, keyof WithErrorMessageComputedProps | 'type'>
>(InputBaseV2);

const FieldTextarea = forwardRef<HTMLTextAreaElement, FieldTextareaProps>((props, ref) => {
  const { autoHeight = true, onChange, value, ...restProps } = props;
  const internalRef = useShareForwardedRef<HTMLTextAreaElement>(ref);
  const [fieldRef, handleChange] = useFieldAutoHeight<HTMLTextAreaElement>(onChange, value);

  useEffect(() => {
    if (autoHeight) {
      fieldRef(internalRef.current);
    }
  }, [autoHeight, fieldRef, internalRef]);

  return (
    <TextareaWithErrorMessage
      {...restProps}
      ref={internalRef}
      value={value}
      onChange={handleChange}
      as="textarea"
    />
  );
});

if (process.env.NODE_ENV === 'development') {
  FieldTextarea.displayName = 'FieldTextarea ';
}

export default FieldTextarea;
